/**
 * Shared HTTP Types and Error Handling
 * Centralized types for HTTP requests, responses, and errors
 */

/**
 * HTTP Error class for typed error handling
 */
export class HttpError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'HttpError';
    Object.setPrototypeOf(this, HttpError.prototype);
  }

  static fromResponse(response: Response, data?: unknown): HttpError {
    return new HttpError(
      response.status,
      response.statusText,
      data && typeof data === 'object' && 'message' in data
        ? String(data.message)
        : response.statusText,
      data
    );
  }

  static fromError(error: unknown): HttpError {
    if (error instanceof HttpError) {
      return error;
    }
    if (error instanceof Error) {
      return new HttpError(500, 'Internal Server Error', error.message);
    }
    return new HttpError(500, 'Internal Server Error', 'An unknown error occurred');
  }

  isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  isServerError(): boolean {
    return this.status >= 500 && this.status < 600;
  }

  isNetworkError(): boolean {
    return this.status === 0;
  }
}

/**
 * HTTP Request configuration
 */
export interface RequestConfig {
  headers?: HeadersInit;
  signal?: AbortSignal;
  timeout?: number;
}

/**
 * HTTP Response type
 */
export interface HttpResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

/**
 * Pagination parameters
 */
export interface PaginationParams {
  page?: number;
  limit?: number;
  offset?: number;
}

/**
 * Paginated response
 */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

/**
 * Query parameters type
 */
export type QueryParams = Record<string, string | number | boolean | (string | number | boolean)[] | null | undefined>;


